"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isMultipleTermInSearch = exports.processMultipleFilterValue = exports.separateSentencesWithBreakline = exports.removeNewLinesAndExcessSpaces = exports.separateDiffBlocksWithBreakline = exports.cleanUnchangedSentencesDiff = exports.getTextDifference = exports.splitTextToSentences = void 0;
const nth_1 = __importDefault(require("lodash-es/nth"));
const diff_match_patch_1 = require("diff-match-patch");
const sbd_1 = require("sbd");
const separators = [".", "...", "!", "?"];
const sentenceSeparators = /\.{1,3} ?$|\? ?$|! ?$/;
const filterDelimetersRegex = /[\s,;']+/;
const splitTextToSentences = (text, 
// eslint-disable-next-line @typescript-eslint/naming-convention
preserve_whitespace = false) => {
    const result = [];
    (0, sbd_1.sentences)(text, { preserve_whitespace })
        .map((sentence) => sentence.replace(/\n/g, ""))
        .forEach((sentence) => {
        if (sentence.startsWith("... ")) {
            result.push("...");
            result.push(sentence.replace("... ", ""));
            return;
        }
        if (!preserve_whitespace) {
            separators.forEach((separator) => {
                if (sentence.endsWith(`${separator} `)) {
                    sentence = sentence.trimRight();
                }
            });
        }
        result.push(sentence);
    });
    return result;
};
exports.splitTextToSentences = splitTextToSentences;
/**
 * Returns a pretty HTML sequence which describes the transformation of previousValue text
 * into currentValue text
 */
const getTextDifference = (previousValue, currentValue, showOnlyChangedSentences = true) => {
    // eslint-disable-next-line new-cap
    const dmp = new diff_match_patch_1.diff_match_patch();
    let diff = dmp.diff_main(previousValue || "", currentValue || "");
    // Makes a diff human-readable. A diff of two unrelated texts can be filled with coincidental
    // matches. For example, the diff of "mouse" and "sofas" is
    // [(-1, "m"), (1, "s"), (0, "o"), (-1, "u"), (1, "fa"), (0, "s"), (-1, "e")].
    // While this is the optimum diff, it is difficult for humans to understand.
    // Semantic cleanup rewrites the diff, expanding it into a more intelligible format.
    // The above example would become: [(-1, "mouse"), (1, "sofas")].
    dmp.diff_cleanupSemantic(diff);
    if (showOnlyChangedSentences) {
        // Processing for displaying only changed sentences each from new line
        diff = (0, exports.separateDiffBlocksWithBreakline)((0, exports.cleanUnchangedSentencesDiff)(diff));
    }
    // Takes a diff array and returns a pretty HTML sequence
    return dmp
        .diff_prettyHtml(diff)
        .replace(/&para;/gm, "")
        .replace(/<ins style="[A-Za-z0-9\S]+">/gm, "<ins>")
        .replace(/<del style="[A-Za-z0-9\S]+">/gm, "<del>");
};
exports.getTextDifference = getTextDifference;
/**
 * Cleans the provided diff from whole unchanged sentences, for example:
 * [[0, 'First sentence. Beginning '],[1, 'added text'],[0, ' end. Third sentence']]
 * ->
 * [[0, 'Beginning '],[1, 'added text'],[0, ' end.']]
 */
const cleanUnchangedSentencesDiff = (diff) => {
    return diff.map((d, index) => {
        var _a;
        const typeOfChange = d[0];
        const changedText = d[1];
        let resultText = "";
        if (typeOfChange !== diff_match_patch_1.DIFF_EQUAL) {
            return [typeOfChange, changedText];
        }
        const prevDiff = index > 0 && diff[index - 1];
        const prevDiffChangedSentences = prevDiff
            ? (0, exports.splitTextToSentences)(prevDiff[1])
            : [];
        const sentences = (0, exports.splitTextToSentences)(changedText, true);
        const firstSentence = sentences[0];
        const lastSentence = (0, nth_1.default)(sentences, -1);
        const isFirstSentenceIncomplete = index > 0 &&
            !sentenceSeparators.test((_a = (0, nth_1.default)(prevDiffChangedSentences, -1)) !== null && _a !== void 0 ? _a : "");
        const isLastSentenceIncomplete = index < diff.length - 1 && !sentenceSeparators.test(lastSentence !== null && lastSentence !== void 0 ? lastSentence : "");
        if (isFirstSentenceIncomplete &&
            isLastSentenceIncomplete &&
            sentences.length > 1) {
            resultText = `${firstSentence}${sentences.length > 2 ? "\n\n" : ""}${lastSentence !== null && lastSentence !== void 0 ? lastSentence : ""}`;
        }
        else if (isFirstSentenceIncomplete) {
            resultText = firstSentence;
        }
        else if (isLastSentenceIncomplete) {
            resultText = lastSentence !== null && lastSentence !== void 0 ? lastSentence : "";
        }
        return [diff_match_patch_1.DIFF_EQUAL, resultText];
    });
};
exports.cleanUnchangedSentencesDiff = cleanUnchangedSentencesDiff;
const separateDiffBlocksWithBreakline = (diff) => {
    // Separate different change blocks with a new line
    return diff
        .map((d, index) => {
        const typeOfChange = d[0];
        const changedText = d[1];
        if (index === 0 || !changedText) {
            return [typeOfChange, changedText];
        }
        const prevDiff = diff[index - 1];
        const prevDiffChangedSentences = (0, exports.splitTextToSentences)(prevDiff[1]);
        // If previous diff block is empty (f.ex. [0, '']), it means it contained fully
        // unchanged sentences that we trim for display and replace with \n\n
        const isPrevBlockEmpty = !prevDiffChangedSentences.length && index > 1;
        return isPrevBlockEmpty
            ? [typeOfChange, `\n\n${changedText}`]
            : [typeOfChange, changedText];
    })
        .filter((d) => !!d[1]);
};
exports.separateDiffBlocksWithBreakline = separateDiffBlocksWithBreakline;
/**
 * Removes new line characters and excess white spaces from a string
 */
const removeNewLinesAndExcessSpaces = (value) => {
    return value.replace(/[\n\r]+|[\s]{2,}/g, " ").trim();
};
exports.removeNewLinesAndExcessSpaces = removeNewLinesAndExcessSpaces;
const separateSentencesWithBreakline = (text) => {
    return (0, exports.splitTextToSentences)(text, true).join("\n\n");
};
exports.separateSentencesWithBreakline = separateSentencesWithBreakline;
/**
 * We allow specifying multiple filter values separated by ;,' and space.
 */
const processMultipleFilterValue = (value, delimetersRegex = filterDelimetersRegex) => {
    return (value !== null && value !== void 0 ? value : "")
        .split(delimetersRegex)
        .filter((_value) => _value.replace(delimetersRegex, "") !== "");
};
exports.processMultipleFilterValue = processMultipleFilterValue;
/**
 * Used to detect if partial search terms (separated by separators) are present in a value
 * Ex.:
 * isMultipleTermInSearch('1,2', '1') should return True
 */
const isMultipleTermInSearch = (searchTerm, value, searchMode = "some") => {
    const lowerTerm = searchTerm.trim().toLowerCase();
    if (!lowerTerm) {
        return false;
    }
    return (0, exports.processMultipleFilterValue)(lowerTerm)
        .map((_term) => value.toLowerCase().includes(_term))[searchMode]((_includes) => !!_includes);
};
exports.isMultipleTermInSearch = isMultipleTermInSearch;
